import React, { useState } from 'react';
import { FormData } from '../types/simulation';

interface SimulationFormProps {
  onSubmit: (data: FormData) => void;
  isLoading: boolean;
  compact?: boolean;
}

const SimulationForm: React.FC<SimulationFormProps> = ({ onSubmit, isLoading, compact = false }) => {
  const [formData, setFormData] = useState<FormData>({
    initialInvestment: '',
    monthlyInvestment: 3,
    years: 30,
    annualReturn: 6.0,
    annualStddev: 15.0,
    trials: 10000,
    // 取り崩し・年金 初期値
    decumulationType: 'none',
    decumulationStartYear: '',
    withdrawalFixedAmount: '',
    withdrawalFixedRateAnnual: '',
    pensionMonthlyAmount: '',
    pensionStartYear: '',
    pensionEndYear: '',
  });
  const [showDecumulation, setShowDecumulation] = useState<boolean>(false);
  const [showPension, setShowPension] = useState<boolean>(false);

  // 年率リターン/標準偏差のプリセット
  const presets: { key: string; name: string; annualReturn: number; annualStddev: number }[] = [
    { key: 'jp_equity', name: '日本株: 5.0% / 18.0%', annualReturn: 5.0, annualStddev: 18.0 },
    { key: 'jp_bond', name: '日本債券: 1.0% / 3.0%', annualReturn: 1.0, annualStddev: 3.0 },
    { key: 'us_equity', name: '米国株: 6.5% / 18.0%', annualReturn: 6.5, annualStddev: 18.0 },
    { key: 'us_bond', name: '米国債券: 3.0% / 6.0%', annualReturn: 3.0, annualStddev: 6.0 },
    { key: 'world_equity', name: '全世界株: 6.0% / 16.0%', annualReturn: 6.0, annualStddev: 16.0 },
    { key: 'world_bond', name: '全世界債券: 2.0% / 5.0%', annualReturn: 2.0, annualStddev: 5.0 },
  ];
  const [selectedPreset, setSelectedPreset] = useState<string>('custom');
  // 初期表示時に最初のプリセットを適用
  React.useEffect(() => {
    const first = presets[0];
    setSelectedPreset(first.key);
    setFormData(prev => ({
      ...prev,
      annualReturn: first.annualReturn,
      annualStddev: first.annualStddev,
    }));
    // presets はリテラル定義で不変のため依存不要
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [errors, setErrors] = useState<Partial<FormData>>({});

  const validateForm = (): boolean => {
    const newErrors: Partial<FormData> = {};

    // 空文字は0として扱う
    const initialInvestment = formData.initialInvestment === '' ? 0 : Number(formData.initialInvestment);
    const monthlyInvestment = formData.monthlyInvestment === '' ? 0 : Number(formData.monthlyInvestment);
    const years = formData.years === '' ? 0 : Number(formData.years);
    const annualReturn = formData.annualReturn === '' ? 0 : Number(formData.annualReturn);
    const annualStddev = formData.annualStddev === '' ? 0 : Number(formData.annualStddev);
    const trials = formData.trials === '' ? 0 : Number(formData.trials);

    // 取り崩し・年金
    const decumulationStartYear = formData.decumulationStartYear === '' ? null : Number(formData.decumulationStartYear);
    const withdrawalFixedAmount = formData.withdrawalFixedAmount === '' ? 0 : Number(formData.withdrawalFixedAmount);
    const withdrawalFixedRateAnnual = formData.withdrawalFixedRateAnnual === '' ? 0 : Number(formData.withdrawalFixedRateAnnual);
    const pensionMonthlyAmount = formData.pensionMonthlyAmount === '' ? 0 : Number(formData.pensionMonthlyAmount);
    const pensionStartYear = formData.pensionStartYear === '' ? null : Number(formData.pensionStartYear);
    const pensionEndYear = formData.pensionEndYear === '' ? null : Number(formData.pensionEndYear);

    if (isNaN(initialInvestment) || initialInvestment < 0) {
      newErrors.initialInvestment = 1 as any;
    }
    if (isNaN(monthlyInvestment) || monthlyInvestment < 0) {
      newErrors.monthlyInvestment = 1 as any;
    }
    // 初期投資額と毎月の投資額の両方が0の場合はエラー
    if (initialInvestment === 0 && monthlyInvestment === 0) {
      newErrors.initialInvestment = 1 as any;
      newErrors.monthlyInvestment = 1 as any;
    }
    if (isNaN(years) || years <= 0 || years > 50) {
      newErrors.years = 1 as any;
    }
    if (isNaN(annualReturn) || annualReturn < -50 || annualReturn > 100) {
      newErrors.annualReturn = 1 as any;
    }
    if (isNaN(annualStddev) || annualStddev < 0 || annualStddev > 200) {
      newErrors.annualStddev = 1 as any;
    }
    if (isNaN(trials) || trials <= 0 || trials > 100000) {
      newErrors.trials = 1 as any;
    }

    // 取り崩しバリデーション（表示・有効なときのみ）
    if (showDecumulation && formData.decumulationType && formData.decumulationType !== 'none') {
      if (decumulationStartYear === null || isNaN(decumulationStartYear) || decumulationStartYear < 0 || decumulationStartYear > years) {
        newErrors.decumulationStartYear = 1 as any;
      }
      if (formData.decumulationType === 'fixed_amount') {
        if (isNaN(withdrawalFixedAmount) || withdrawalFixedAmount <= 0) {
          newErrors.withdrawalFixedAmount = 1 as any;
        }
      }
      if (formData.decumulationType === 'fixed_rate') {
        if (isNaN(withdrawalFixedRateAnnual) || withdrawalFixedRateAnnual <= 0 || withdrawalFixedRateAnnual > 100) {
          newErrors.withdrawalFixedRateAnnual = 1 as any;
        }
      }
    }

    // 年金バリデーション（表示・有効なときのみ）
    if (showPension) {
      if (isNaN(pensionMonthlyAmount) || pensionMonthlyAmount < 0) {
        newErrors.pensionMonthlyAmount = 1 as any;
      }
      if (pensionStartYear !== null) {
        if (isNaN(pensionStartYear) || pensionStartYear < 0 || pensionStartYear > years) {
          newErrors.pensionStartYear = 1 as any;
        }
        if (pensionEndYear !== null && (isNaN(pensionEndYear) || pensionEndYear < pensionStartYear || pensionEndYear > years)) {
          newErrors.pensionEndYear = 1 as any;
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  const handleInputChange = (field: keyof FormData, value: string) => {
    // 空文字は許可し、数値変換は後で行う
    const processedValue = value === '' ? '' : Number(value);
    
    setFormData(prev => ({ ...prev, [field]: processedValue }));
    // エラーをクリア
    if ((errors as any)[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleSelectChange = (field: keyof FormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value as any }));
    if ((errors as any)[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handlePresetChange = (value: string) => {
    setSelectedPreset(value);
    const preset = presets.find(p => p.key === value);
    if (!preset || value === 'custom') return;
    setFormData(prev => ({
      ...prev,
      annualReturn: preset.annualReturn,
      annualStddev: preset.annualStddev,
    }));
    // エラークリア
    setErrors(prev => ({ ...prev, annualReturn: undefined, annualStddev: undefined }));
  };

  return (
    <div className="card rounded-br-none">
      <h2 className="text-2xl font-bold text-gray-800 mb-6">📊 積立投資シミュレーション</h2>
      
      <form onSubmit={handleSubmit} className={compact ? 'space-y-3' : 'space-y-6'}>
        {/* 条件と年率プリセット（横並び2カラム） */}
        <div className={`grid grid-cols-1 lg:grid-cols-2 ${compact ? 'gap-3' : 'gap-6'}`}>
          {/* 左：投資条件ボックス（初期/毎月/年数） */}
          <div className={`border rounded-lg ${compact ? 'p-3' : 'p-4'} bg-gray-50`}>
            <label className="block text-sm font-medium text-gray-700 mb-2">投資条件</label>
            <div className={`grid grid-cols-1 md:grid-cols-3 ${compact ? 'gap-3' : 'gap-4'}`}>
              {/* 初期投資額 */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-2">初期投資（万円）</label>
                <input
                  type="number"
                  value={formData.initialInvestment}
                  onChange={(e) => handleInputChange('initialInvestment', e.target.value)}
                  className={`input-field w-full ${errors.initialInvestment ? 'border-red-500' : ''}`}
                  placeholder="0"
                  min="0"
                  step="1"
                />
              </div>
              {/* 毎月の投資額 */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-2">毎月積立（万円）</label>
                <input
                  type="number"
                  value={formData.monthlyInvestment}
                  onChange={(e) => handleInputChange('monthlyInvestment', e.target.value)}
                  className={`input-field w-full ${errors.monthlyInvestment ? 'border-red-500' : ''}`}
                  placeholder="3"
                  min="0"
                  step="1"
                />
              </div>
              {/* 投資期間 */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-2">投資期間（年）</label>
                <input
                  type="number"
                  value={formData.years}
                  onChange={(e) => handleInputChange('years', e.target.value)}
                  className={`input-field w-full ${errors.years ? 'border-red-500' : ''}`}
                  placeholder="30"
                  min="1"
                  max="50"
                  step="1"
                />
              </div>
            </div>
          </div>

          {/* 右：年率パラメータ プリセット（半幅） */}
          <div className={`border rounded-lg ${compact ? 'p-3' : 'p-4'} bg-gray-50`}>
            <label className="block text-sm font-medium text-gray-700 mb-2">年率パラメータ（リターン/リスク）</label>
            <div className="flex gap-2">
              <select
                value={selectedPreset}
                onChange={(e) => handlePresetChange(e.target.value)}
                className="input-field w-full"
              >
                {presets.map(p => (
                  <option key={p.key} value={p.key}>{p.name}</option>
                ))}
              </select>
              <button
                type="button"
                className="btn-secondary whitespace-nowrap"
                onClick={() => {
                  setSelectedPreset('custom');
                }}
              >
                カスタムに戻す
              </button>
            </div>
            {/* 年率入力（横並び） */}
            <div className={`${compact ? 'mt-2' : 'mt-4'} grid grid-cols-1 md:grid-cols-2 ${compact ? 'gap-3' : 'gap-4'}`}>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">年間期待リターン（%）</label>
                <input
                  type="number"
                  value={formData.annualReturn}
                  onChange={(e) => handleInputChange('annualReturn', e.target.value)}
                  className={`input-field w-full ${errors.annualReturn ? 'border-red-500' : ''}`}
                  placeholder="6.0"
                  min="-50"
                  max="100"
                  step="0.1"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">年間標準偏差（%）</label>
                <input
                  type="number"
                  value={formData.annualStddev}
                  onChange={(e) => handleInputChange('annualStddev', e.target.value)}
                  className={`input-field w-full ${errors.annualStddev ? 'border-red-500' : ''}`}
                  placeholder="15.0"
                  min="0"
                  max="200"
                  step="0.1"
                />
              </div>
            </div>
            <div className={`text-xs text-gray-600 ${compact ? 'mt-2' : 'mt-3'}`}>
              <span className="mr-3">設定値プレビュー:</span>
              <span className="mr-2">期待リターン {formData.annualReturn === '' ? '-' : `${formData.annualReturn}%`}</span>
              <span>標準偏差 {formData.annualStddev === '' ? '-' : `${formData.annualStddev}%`}</span>
            </div>
          </div>
        </div>

        

        {/* 取り崩し設定（折りたたみ） */}
        <div className={`border rounded-lg ${compact ? 'p-3' : 'p-4'} bg-gray-50`}>
          <button
            type="button"
            className="w-full flex justify-between items-center text-left"
            onClick={() => setShowDecumulation(v => !v)}
          >
            <span className="block text-sm font-medium text-gray-700">取り崩し設定（任意）</span>
            <span className="text-gray-500 text-sm">{showDecumulation ? '▲ 閉じる' : '▼ 開く'}</span>
          </button>
          {showDecumulation && (
            <div className={compact ? 'mt-2' : 'mt-4'}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-700 mb-2">取り崩し方式</label>
                  <select
                    value={formData.decumulationType || 'none'}
                    onChange={(e) => handleSelectChange('decumulationType', e.target.value)}
                    className="input-field w-full"
                  >
                    <option value="none">取り崩しなし</option>
                    <option value="fixed_amount">定額（毎月）</option>
                    <option value="fixed_rate">定率（年率）</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-gray-700 mb-2">取り崩し開始年</label>
                  <input
                    type="number"
                    value={formData.decumulationStartYear}
                    onChange={(e) => handleInputChange('decumulationStartYear', e.target.value)}
                    className={`input-field w-full ${errors.decumulationStartYear ? 'border-red-500' : ''}`}
                    placeholder="例: 30"
                    min="0"
                    max={formData.years || 50 as any}
                    step="1"
                  />
                  {errors.decumulationStartYear && (
                    <p className="text-red-500 text-sm mt-1">0〜投資年数の範囲で入力してください</p>
                  )}
                </div>
              </div>

              {formData.decumulationType === 'fixed_amount' && (
                <div className={compact ? 'mt-2' : 'mt-4'}>
                  <label className="block text-sm text-gray-700 mb-2">毎月の取り崩し額（万円）</label>
                  <input
                    type="number"
                    value={formData.withdrawalFixedAmount}
                    onChange={(e) => handleInputChange('withdrawalFixedAmount', e.target.value)}
                    className={`input-field w-full ${errors.withdrawalFixedAmount ? 'border-red-500' : ''}`}
                    placeholder="例: 20"
                    min="0"
                    step="1"
                  />
                  {errors.withdrawalFixedAmount && (
                    <p className="text-red-500 text-sm mt-1">1以上の値を入力してください（万円）</p>
                  )}
                </div>
              )}

              {formData.decumulationType === 'fixed_rate' && (
                <div className={compact ? 'mt-2' : 'mt-4'}>
                  <label className="block text-sm text-gray-700 mb-2">取り崩し率（年率, %）</label>
                  <input
                    type="number"
                    value={formData.withdrawalFixedRateAnnual}
                    onChange={(e) => handleInputChange('withdrawalFixedRateAnnual', e.target.value)}
                    className={`input-field w-full ${errors.withdrawalFixedRateAnnual ? 'border-red-500' : ''}`}
                    placeholder="例: 4"
                    min="0"
                    max="100"
                    step="0.1"
                  />
                  {errors.withdrawalFixedRateAnnual && (
                    <p className="text-red-500 text-sm mt-1">0より大きく100以下で入力してください（%）</p>
                  )}
                </div>
              )}

              <p className="text-gray-500 text-xs mt-3">※ 取り崩しは資産から差し引かれます。開始年を指定してください。</p>
            </div>
          )}
        </div>

        {/* 年金設定（折りたたみ） */}
        <div className={`border rounded-lg ${compact ? 'p-3' : 'p-4'} bg-gray-50`}>
          <button
            type="button"
            className="w-full flex justify-between items-center text-left"
            onClick={() => setShowPension(v => !v)}
          >
            <span className="block text-sm font-medium text-gray-700">年金設定（任意）</span>
            <span className="text-gray-500 text-sm">{showPension ? '▲ 閉じる' : '▼ 開く'}</span>
          </button>
          {showPension && (
            <div className={compact ? 'mt-2' : 'mt-4'}>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm text-gray-700 mb-2">年金（月額, 万円）</label>
                  <input
                    type="number"
                    value={formData.pensionMonthlyAmount}
                    onChange={(e) => handleInputChange('pensionMonthlyAmount', e.target.value)}
                    className={`input-field w-full ${errors.pensionMonthlyAmount ? 'border-red-500' : ''}`}
                    placeholder="例: 15"
                    min="0"
                    step="1"
                  />
                  {errors.pensionMonthlyAmount && (
                    <p className="text-red-500 text-sm mt-1">0以上で入力してください（万円）</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm text-gray-700 mb-2">開始年</label>
                  <input
                    type="number"
                    value={formData.pensionStartYear}
                    onChange={(e) => handleInputChange('pensionStartYear', e.target.value)}
                    className={`input-field w-full ${errors.pensionStartYear ? 'border-red-500' : ''}`}
                    placeholder="例: 65"
                    min="0"
                    max={formData.years || 50 as any}
                    step="1"
                  />
                  {errors.pensionStartYear && (
                    <p className="text-red-500 text-sm mt-1">0〜投資年数の範囲で入力してください</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm text-gray-700 mb-2">終了年（任意）</label>
                  <input
                    type="number"
                    value={formData.pensionEndYear}
                    onChange={(e) => handleInputChange('pensionEndYear', e.target.value)}
                    className={`input-field w-full ${errors.pensionEndYear ? 'border-red-500' : ''}`}
                    placeholder="例: 90"
                    min="0"
                    max={formData.years || 50 as any}
                    step="1"
                  />
                  {errors.pensionEndYear && (
                    <p className="text-red-500 text-sm mt-1">開始年以上かつ投資年数以下で入力してください</p>
                  )}
                </div>
              </div>
              <p className="text-gray-500 text-xs mt-3">※ 年金は指定期間のみ資産に加算されます。未指定時のデフォルトは全期間です。</p>
            </div>
          )}
        </div>

        {/* 試行回数 + 実行ボタン（横並び） */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="md:col-span-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">シミュレーション試行回数</label>
            <select
              value={formData.trials}
              onChange={(e) => handleInputChange('trials', e.target.value)}
              className="input-field w-full"
            >
              <option value={1000}>1,000回（高速）</option>
              <option value={5000}>5,000回（標準）</option>
              <option value={10000}>10,000回（推奨）</option>
              <option value={50000}>50,000回（高精度）</option>
            </select>
          </div>
          <div className="md:col-span-2 flex items-end">
            <button
              type="button"
              disabled={isLoading}
              onClick={(e) => {
                if (!isLoading) {
                  const mockEvent = { preventDefault: () => {} } as React.FormEvent;
                  handleSubmit(mockEvent);
                }
              }}
              className={`btn-primary w-full py-3 text-lg ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {isLoading ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  シミュレーション実行中...
                </span>
              ) : (
                '🚀 シミュレーション実行'
              )}
            </button>
          </div>
        </div>

      </form>

      {/* 説明は結果セクション側に移動（ここでは表示しない） */}
    </div>
  );
};

export default SimulationForm; 